import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import FactionSectionsEditor from '../../components/FactionSectionsEditor/FactionSectionsEditor';
import api from '../../services/api';
import factionService from '../../services/factionService';
import universeService from '../../services/universeService';
import { collectForumMoveOptions } from '../../utils/forumMoveOptions';
import {
  ALIGNMENT_OPTIONS,
  SCOPE_OPTIONS,
  createDefaultSections,
  normalizeSections,
} from '../../utils/factionMeta';
import styles from './Factions.module.css';

const DEFAULT_FORM = {
  name: '',
  universe_id: '',
  alignment: '',
  scope: '',
  status: 'open',
  logo: '',
  icon: '',
  headquartersForumId: '',
  sections: createDefaultSections(),
};

const FactionForm = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [universes, setUniverses] = useState([]);
  const [forumOptions, setForumOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingField, setUploadingField] = useState(null);
  const logoInputRef = useRef(null);
  const iconInputRef = useRef(null);

  const selectedUniverse = useMemo(
    () => universes.find((universe) => String(universe.id) === String(form.universe_id)),
    [universes, form.universe_id]
  );

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [universesData, factionData] = await Promise.all([
          universeService.getUniverses(),
          isEditing ? factionService.getFaction(id) : Promise.resolve(null),
        ]);
        setUniverses(universesData || []);
        if (factionData) {
          setForm({
            name: factionData.name || '',
            universe_id: factionData.universe?.id ? String(factionData.universe.id) : '',
            alignment: factionData.alignment || '',
            scope: factionData.scope || '',
            status: factionData.status || 'open',
            logo: factionData.logo || '',
            icon: factionData.icon || '',
            headquartersForumId: factionData.headquartersForum?.id
              ? String(factionData.headquartersForum.id)
              : '',
            sections: normalizeSections(factionData.sections),
          });
        }
      } catch (err) {
        setError('Erreur lors du chargement du formulaire');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, isEditing]);

  useEffect(() => {
    const loadForums = async () => {
      if (!selectedUniverse?.slug) {
        setForumOptions([]);
        return;
      }

      try {
        const forumsData = await universeService.getUniversForums(selectedUniverse.slug);
        setForumOptions(collectForumMoveOptions(forumsData));
      } catch (err) {
        setForumOptions([]);
      }
    };

    loadForums();
  }, [selectedUniverse?.slug]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSectionsChange = (sections) => {
    setForm((prev) => ({ ...prev, sections }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        name: form.name,
        universe_id: Number(form.universe_id),
        alignment: form.alignment || null,
        scope: form.scope || null,
        status: form.status,
        logo: form.logo || null,
        icon: form.icon || null,
        headquartersForumId: form.headquartersForumId ? Number(form.headquartersForumId) : null,
        sections: form.sections.map((section, index) => ({
          id: section.id ?? undefined,
          title: index === 0 ? 'Description' : section.title,
          content: section.content || '',
        })),
      };

      if (isEditing) {
        await factionService.updateFaction(id, payload);
        navigate(`/factions/${id}`);
      } else {
        const response = await factionService.createFaction(payload);
        const createdId = response?.faction?.id;
        navigate(createdId ? `/factions/${createdId}` : '/factions');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (targetField, file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image valide.');
      return;
    }

    setUploadingField(targetField);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/api/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (!response.data?.url) {
        throw new Error('URL de média manquante');
      }

      setForm((prev) => ({ ...prev, [targetField]: response.data.url }));
    } catch (uploadError) {
      console.error(uploadError);
      alert(uploadError.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setUploadingField(null);
      if (targetField === 'logo' && logoInputRef.current) {
        logoInputRef.current.value = '';
      }
      if (targetField === 'icon' && iconInputRef.current) {
        iconInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <Loading message="Chargement du formulaire..." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.contentWide}>
        <Breadcrumb items={[{ name: 'Accueil', url: '/', icon: 'home' }, { name: 'Factions', url: '/factions' }, { name: isEditing ? 'Modifier' : 'Créer', url: null }]} />
        <div className={styles.formCard}>
          <h1>{isEditing ? 'Modifier une faction' : 'Créer une faction'}</h1>
          <p className={styles.formSubtitle}>
            Renseignez les informations principales, reliez le quartier général à un forum RP, puis composez vos sections de contenu.
          </p>

          {error && <p className={styles.formError}>{error}</p>}

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.fieldGrid}>
              <label className={styles.field}>
                <span>
                  Nom <em className={styles.required}>obligatoire</em>
                </span>
                <input name="name" value={form.name} onChange={handleChange} required className={styles.input} />
              </label>

              <label className={styles.field}>
                <span>
                  Univers <em className={styles.required}>obligatoire</em>
                </span>
                <select name="universe_id" value={form.universe_id} onChange={handleChange} required className={styles.input}>
                  <option value="">Sélectionner</option>
                  {universes.map((universe) => (
                    <option key={universe.id} value={universe.id}>{universe.name}</option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span>Alignement</span>
                <select name="alignment" value={form.alignment} onChange={handleChange} className={styles.input}>
                  <option value="">Sélectionner</option>
                  {ALIGNMENT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span>Portée</span>
                <select name="scope" value={form.scope} onChange={handleChange} className={styles.input}>
                  <option value="">Sélectionner</option>
                  {SCOPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span>Statut</span>
                <select name="status" value={form.status} onChange={handleChange} className={styles.input}>
                  <option value="open">Ouverte</option>
                  <option value="closed">Fermée</option>
                </select>
              </label>

              <label className={styles.field}>
                <span>Quartier général (forum RP)</span>
                <select
                  name="headquartersForumId"
                  value={form.headquartersForumId}
                  onChange={handleChange}
                  className={styles.input}
                  disabled={!selectedUniverse}
                >
                  <option value="">Aucun forum lié</option>
                  {forumOptions.map((forum) => (
                    <option key={forum.id} value={forum.id}>{forum.label}</option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span>URL du logo</span>
                <div className={styles.mediaInputRow}>
                  <input
                    name="logo"
                    value={form.logo}
                    onChange={handleChange}
                    className={styles.input}
                    placeholder="https://..."
                  />
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingField === 'logo'}
                  >
                    {uploadingField === 'logo' ? 'Upload...' : 'Upload'}
                  </button>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className={styles.hiddenFileInput}
                    onChange={(event) => handleUpload('logo', event.target.files?.[0])}
                  />
                </div>
              </label>

              <label className={styles.field}>
                <span>URL de l'icône</span>
                <div className={styles.mediaInputRow}>
                  <input
                    name="icon"
                    value={form.icon}
                    onChange={handleChange}
                    className={styles.input}
                    placeholder="https://..."
                  />
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={() => iconInputRef.current?.click()}
                    disabled={uploadingField === 'icon'}
                  >
                    {uploadingField === 'icon' ? 'Upload...' : 'Upload'}
                  </button>
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/*"
                    className={styles.hiddenFileInput}
                    onChange={(event) => handleUpload('icon', event.target.files?.[0])}
                  />
                </div>
              </label>
            </div>

            {(form.logo || form.icon) && (
              <div className={styles.logoPreview}>
                {form.logo && (
                  <div className={styles.previewBlock}>
                    <p>Aperçu logo</p>
                    <img src={form.logo} alt="Aperçu du logo de faction" className={styles.previewImage} />
                  </div>
                )}
                {form.icon && (
                  <div className={styles.previewBlock}>
                    <p>Aperçu icône</p>
                    <img src={form.icon} alt="Aperçu de l'icône de faction" className={styles.previewIcon} />
                  </div>
                )}
              </div>
            )}

            <FactionSectionsEditor sections={form.sections} onChange={handleSectionsChange} />

            <div className={styles.formActions}>
              <button type="button" className={styles.secondaryBtn} onClick={() => navigate(isEditing ? `/factions/${id}` : '/factions')}>
                Annuler
              </button>
              <button type="submit" className={styles.primaryBtn} disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default FactionForm;
